import { useDispatch } from 'react-redux';

// Types
import {
  CollectionPointProps,
  SelectCollectionPointParams,
} from 'types/CollectionPoint.types';
import { AppDispatch } from 'types/RootState.types';
import { ClickAndCollectResultsProps } from './ClickAndCollectResults.types';

// Config
import { VIEW_TYPE_LIST, VIEW_TYPE_MAP } from '../../../constants/clickCollectConstants';
import { useWindowSize } from '../../../context/window/WindowSizeContext';
import {
  selectCollectionPoint,
  setActiveMapMarker,
} from '../../../redux/actions/click-and-collect/clickAndCollectActions';
import { showMoreShops } from '../../../redux/actions/app/appActions';

// Components
import ClickAndCollectChargesTooltip from '../../../components/click-and-collect-charges-tooltip';
import ClickAndCollectCountdown from '../../../components/click-and-collect-countdown';
import ClickAndCollectSearchListView from '../../../components/click-and-collect-search-list-view';
import ClickAndCollectSearchMapView from '../../../components/click-and-collect-search-map-view';
import ClickAndCollectViewTabs from '../../../components/click-and-collect-view-tabs';
import Container from '../../../components/container';
import Heading from '../../../components/heading';

// Styles
import styles from './click-and-collect-results.scss';

const ClickAndCollectResults = ({
  activeMapMarker,
  activeViewType = VIEW_TYPE_LIST,
  collectionPointSearchComplete = false,
  collectionPointSearchResults = [],
  collectionPointsOwners = [],
  countdownVisible = false,
  earliestCollectionDateTime,
  cutOffTimeForAllStores,
  searchTerm,
  disabled = false,
  getCollectionPointDatesApiCallActive = false,
  isFreeCollection = true,
  selectedCollectionPoint,
  selectingCollectionPointId,
  shopsToShow,
  storeToPromote,
  totalCollectionPoints = 0,
}: ClickAndCollectResultsProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { desktopResolution } = useWindowSize();

  const selectCollectionPointHandler = (params: SelectCollectionPointParams) => {
    dispatch(selectCollectionPoint(params));
  };

  const showMoreShopsHandler = (updateShopsToShow: number) => {
    dispatch(showMoreShops(updateShopsToShow));
  };

  const setActiveMapMarkerHandler = (collectionPoint: CollectionPointProps) => {
    dispatch(setActiveMapMarker(collectionPoint));
  };

  return collectionPointSearchComplete && totalCollectionPoints > 0 && (
    <>
      <Container testId="click-collect-search-complete-container" width="full">
        <div>
          <Container
            flex={desktopResolution ? { alignItems: 'center', gap: '6', justifyContent: 'space-between' } : undefined}
            marginTop="4"
          >
            <Heading
              className={styles.resultsCount}
              label={`Your results for ‘${searchTerm}’`}
              marginBottom="2"
              tag="h4"
              testId="click-collect-results-count-title"
              type="s"
            />
            <ClickAndCollectViewTabs disabled={disabled} />
          </Container>

          {
            !selectedCollectionPoint &&
            <ClickAndCollectCountdown
              className="mb-3"
              collectFromTime={earliestCollectionDateTime}
              cutOffTime={cutOffTimeForAllStores}
            />
          }

          {!isFreeCollection && (
            <ClickAndCollectChargesTooltip
              className={styles.clickCollectAboutCharges}
              collectionPointsOwners={collectionPointsOwners}
            />
          )}
        </div>
      </Container>

      {activeViewType === VIEW_TYPE_LIST && (
        <ClickAndCollectSearchListView
          collectionPointSearchResults={collectionPointSearchResults}
          countdownVisible={countdownVisible}
          data-testid="click-and-collect-search-list-view"
          disabled={disabled}
          getCollectionPointDatesApiCallActive={getCollectionPointDatesApiCallActive}
          selectCollectionPoint={selectCollectionPointHandler}
          selectedCollectionPoint={selectedCollectionPoint}
          selectingCollectionPointId={selectingCollectionPointId}
          shopsToShow={shopsToShow}
          showMoreShops={showMoreShopsHandler}
          storeToPromote={storeToPromote}
        />
      )}

      {activeViewType === VIEW_TYPE_MAP && (
        <ClickAndCollectSearchMapView
          activeMapMarker={activeMapMarker}
          collectionPointSearchResults={collectionPointSearchResults}
          data-testid="click-and-collect-search-map-view"
          disabled={disabled}
          getCollectionPointDatesApiCallActive={getCollectionPointDatesApiCallActive}
          searchTerm={searchTerm}
          selectCollectionPoint={selectCollectionPointHandler}
          selectedCollectionPoint={selectedCollectionPoint}
          setActiveMapMarker={setActiveMapMarkerHandler}
        />
      )}
    </>
  );
};

export default ClickAndCollectResults;
